import { useQueryClient } from '@tanstack/react-query';
import { rolesManipulator, useGetUserRoleBusinessesIDs } from 'auth';
import { ReactNode, useMemo } from 'react';
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import {
  SECRET_MANAGER_SECRETS_QUERY_KEY,
  SECRET_MANAGER_VAULT_QUERY_KEY,
  SECRET_MANAGER_VAULTS_QUERY_KEY,
} from '@/modules/common/queries/secret-manager.ts';
import { CUSTOM_CHARTS_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/custom-charts.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { employeeDashboardPaths } from '@/modules/employee-dashboard/routes.tsx';
import { DropdownMenuItem } from 'ui';

export const useSidebarHeaderMenuOptions = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useCurrentUserQuery();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const availableRegistersIds = useGetUserRoleBusinessesIDs(rolesManipulator.current_user_role);

  const items = useMemo<(DropdownMenuItem & { labelCurrent?: ReactNode })[]>(
    () =>
      (data?.businesses || [])
        .filter(({ id }) => availableRegistersIds.includes(id))
        .map(business => ({
          label: business.name,
          labelCurrent: (
            <div className="flex gap-3 items-center pr-3">
              {business?.icon_url && (
                <span className="bg-white border-white border">
                  <img src={business?.icon_url} alt="icon" className="w-6 h-6 max-w-6 max-h-6" />
                </span>
              )}
              <span>{business?.name || ''}</span>
            </div>
          ),
          onClick: () => {
            params.set(BUSINESS_QUERY_PARAM, String(business.id));

            const isOnClientDetailPage = !!matchPath(
              adminDashboardPaths.dynamic.client(),
              location.pathname,
            );
            const isOnAdminSecretVaultPage = !!matchPath(
              adminDashboardPaths.dynamic.secretVault(),
              location.pathname,
            );
            const isOnEmployeeSecretVaultPage = !!matchPath(
              employeeDashboardPaths.dynamic.secretVault(),
              location.pathname,
            );

            if (isOnClientDetailPage) {
              navigate({
                pathname: adminDashboardPaths.static.clients,
                search: params.toString(),
              });
            } else if (isOnAdminSecretVaultPage) {
              navigate({
                pathname: adminDashboardPaths.static.secretManager,
                search: params.toString(),
              });
            } else if (isOnEmployeeSecretVaultPage) {
              navigate({
                pathname: employeeDashboardPaths.static.secretManager,
                search: params.toString(),
              });
            } else {
              setParams(params);
            }

            queryClient.invalidateQueries({ queryKey: [CUSTOM_CHARTS_LIST_QUERY_KEY] });
            queryClient.removeQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
            queryClient.removeQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
            queryClient.removeQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
          },
          value: business.id.toString(),
          // icon: null,
          hoverRightButton: null,
        })),
    [
      availableRegistersIds,
      data?.businesses,
      location.pathname,
      navigate,
      params,
      queryClient,
      setParams,
    ],
  );

  return { items, isLoading };
};
